// Nuke the OPFS origin root — every file and directory below it.
// Used by the "Wipe app" flow on MasterUnlockScreen and mirrored by
// the end-to-end Playwright helper in `web/e2e/helpers/master-setup.ts`.
//
// Notes on races:
//   - OPFS handles held by the Wasm worker may still be mapped to
//     files under the vault root. Call `lockVault` first so the
//     worker drops the `UnlockedVolume` (which owns the adapter's
//     `FileSystemSyncAccessHandle`s). Even then, a
//     `FileSystemSyncAccessHandle` that hasn't been `close()`d yet
//     will make `removeEntry` throw `NoModificationAllowedError`.
//     Rather than ping-pong with the worker, we just retry a couple
//     of times with a small delay.
//   - The bare `for await (const … of root)` on a
//     `FileSystemDirectoryHandle` yields `[name, handle]` tuples
//     per the WHATWG FileSystem spec — not plain names. We use
//     `root.keys()` explicitly below so the iterator yields just the
//     string name, then snapshot the names up front and THEN remove
//     so an in-flight remove doesn't invalidate the iterator.

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 100;

// Structural view of `FileSystemDirectoryHandle` — `keys()` returns
// names cleanly (the bare async-iterable yields `[name, handle]`
// tuples per the WHATWG FileSystem spec, which we never want here).
interface OpfsRoot {
  keys(): AsyncIterable<string>;
  removeEntry: (name: string, opts: { recursive: boolean }) => Promise<void>;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Snapshot every top-level entry name under `root`. */
async function collectRootEntryNames(root: OpfsRoot): Promise<string[]> {
  const names: string[] = [];
  for await (const name of root.keys()) {
    names.push(name);
  }
  return names;
}

/**
 * Remove a single top-level entry, retrying `NoModificationAllowedError`
 * briefly (a `FileSystemSyncAccessHandle` the Wasm worker hasn't
 * closed yet triggers this) and swallowing `NotFoundError` (another
 * caller raced us or the entry is phantom). Unknown failures are
 * rethrown immediately.
 */
async function removeEntryWithRetry(
  root: OpfsRoot,
  name: string,
): Promise<void> {
  let lastError: unknown = null;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt += 1) {
    try {
      await root.removeEntry(name, { recursive: true });
      return;
    } catch (err) {
      const errName = (err as { name?: string } | null)?.name;
      if (errName === "NotFoundError") {
        return;
      }
      if (errName !== "NoModificationAllowedError") {
        throw err;
      }
      lastError = err;
      if (attempt < MAX_RETRIES - 1) {
        await sleep(RETRY_DELAY_MS);
      }
    }
  }
  throw lastError;
}

/**
 * Remove every top-level entry from the OPFS origin root. Each
 * `removeEntry` runs with `recursive: true`. Safe to call even when
 * the root is empty. Per-entry `NotFoundError` is ignored (another
 * caller may have raced us); `NoModificationAllowedError` retries
 * with a brief backoff.
 */
export async function wipeOpfs(): Promise<void> {
  const root = (await navigator.storage.getDirectory()) as unknown as OpfsRoot;
  const names = await collectRootEntryNames(root);
  for (const name of names) {
    await removeEntryWithRetry(root, name);
  }
}
